import { DatePipe } from "@angular/common";
import { computed, inject, NgZone, signal } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { GridOptions } from "ag-grid-enterprise";
import { PastTransactionTableSubscription } from "@bitwarden/web-vault/app/components/tables/transaction-table/past-transaction-table.subscription";
import {
  DATA_ROW_HEIGHT,
  getOptions,
  GROUP_ROW_HEIGHT,
  TableRow,
  TableSource,
  getTransactionTableColumns,
} from "@bitwarden/web-vault/app/components/tables/transaction-table/transaction-table";
import {
  PastGroupIntro,
  planFlatPhase,
} from "@bitwarden/web-vault/app/components/tables/transaction-table/past-group-intro.motion";
import { GranularityFilter } from "@bitwarden/web-vault/app/gloss/views/views-control/views-customize/granularity.filter";
import { GranularityProperty } from "@bitwarden/web-vault/app/models/types/balanceGroupingTypes";
import { prefersReducedMotion } from "@bitwarden/web-vault/app/shared/utils/reduced-motion.util";
import type { CellClassParams, ColDef, GridApi, ValueGetterParams } from "ag-grid-enterprise";
import { PastGroupHeaderComponent } from "../renderers/row-group/transaction-table/past-transaction-table/past-group-header.component";
import { TransactionInfoPanelComponent } from "../renderers/row-detail/transaction-info-panel/transaction-info-panel.component";
import { View as ViewEnum } from "@bitwarden/web-vault/app/models/enum/view.enum";

// ag-Grid reports the first data rendered once a row exists, which an empty list never gets. If nothing has
// started the intro this long after the grid is ready, it starts anyway when there are rows (slow to render), and
// is dropped when there are none (nothing to animate), so the skeleton never sits over an empty grid.
const NO_ROWS_GRACE_MS = 800;

export class PastTransactionTable extends PastTransactionTableSubscription {
  granularityFilter = inject(GranularityFilter);
  dialog = inject(MatDialog);
  private datePipe = inject(DatePipe);
  private zone = inject(NgZone);
  options: GridOptions<TableRow>;

  // #5637 list intro (see past-group-intro.motion.ts). It plays every time the Past list is shown. It is armed
  // before the grid exists, so the first frame is already the flat list (the phase class reaches the grid host
  // through `motionClass`) instead of flashing the grouped one. It is cleared once played, and re-armed
  // whenever the grid goes away so the next visit starts flat again.
  private readonly intro = signal<PastGroupIntro | null>(null);
  // Reduced motion: no flat phase, the grouped list just fades in each time it is shown.
  private readonly reducedFade = signal(false);
  // The replay re-arms the grid it is showing, so it needs the api (set once the grid exists).
  private gridApi: GridApi | null = null;

  /** Phase class for the grid host; bind to AgGridTableComponent's `customGridClass`. */
  readonly motionClass = computed(() => {
    const intro = this.intro();
    if (intro) {
      return `past-intro-${intro.phase()}`;
    }
    return this.reducedFade() ? "past-intro-reduced" : "";
  });

  /** True from the moment the intro is armed until it ends: the template keeps the skeleton over the grid. */
  readonly introActive = computed(() => this.intro() !== null);

  /** True while the list is still building underneath the skeleton (the grid itself is hidden). */
  readonly introPending = computed(() => this.intro()?.phase() === "pending");

  // What the grid shows right now, so it can be held while the intro plays.
  private shownData: TableSource[] | undefined;

  /**
   * The row data for the grid; bind to AgGridTableComponent's `data`. The stores push new row data now and then
   * (a few times right after a cold load), and each push makes ag-Grid rebuild every row, which would tear the
   * intro down mid-way. So once the list has been revealed, the grid keeps what it has until the intro is over
   * and then gets the latest. Before that (the list is still building, hidden) and with no intro, it is live.
   */
  readonly gridData = computed(() => {
    const live = this.data();
    const phase = this.intro()?.phase();
    const holding = phase === "flat" || phase === "run";
    if (!holding || this.shownData === undefined) {
      this.shownData = live;
    }
    return this.shownData;
  });

  constructor() {
    super();
    this.armIntro();

    const baseOptions = getOptions(this.dialog, ViewEnum.PAST);
    const baseRowHeight = baseOptions.getRowHeight;
    this.options = {
      ...baseOptions,
      suppressContextMenu: true,
      components: {
        appPastGroupHeader: PastGroupHeaderComponent,
        transactionInfoPanel: TransactionInfoPanelComponent,
      },
      groupRowRenderer: "appPastGroupHeader",
      groupRowRendererParams: {
        suppressCount: true,
        isIntroCollapsing: (groupKey: string) => this.intro()?.isCollapsing(groupKey) ?? false,
        isIntroHeaderPending: (groupKey: string) =>
          this.intro()?.isHeaderPending(groupKey) ?? false,
      },
      getRowHeight: (params) => this.intro()?.rowHeight(params.node) ?? baseRowHeight?.(params),
      isGroupOpenByDefault: (params) => this.intro()?.isOpenByDefault(params.key) ?? false,
      // While the intro's flat phase is up this limits the rows that render to what fits the viewport:
      // every row costs an Angular cell renderer, and ag-Grid builds those a frame at a time.
      isExternalFilterPresent: () => this.intro()?.isFiltering ?? false,
      doesExternalFilterPass: (node) => this.intro()?.passes(node) ?? true,
      onGridReady: (event) => {
        this.gridApi = event.api;
        this.guardAgainstNoRows(event.api);
      },
      // The stores update the row data a couple of times right after the grid appears; the intro waits for that.
      onRowDataUpdated: () => this.intro()?.noteDataChange(),
      onFirstDataRendered: (event) => this.onFirstDataRendered(event.api),
      onGridPreDestroyed: () => this.onGridPreDestroyed(),
      columnDefs: this.getPastTransactionTableColumns(),
    };
  }

  private createIntro(): PastGroupIntro {
    return new PastGroupIntro({
      plan: () =>
        planFlatPhase(
          (this.gridData() as TableRow[]).map((row) => row.transactionDate),
          (date) =>
            this.getPastGranularityGroupKey(
              date,
              this.granularityFilter.selectedGranularity() as GranularityProperty,
            ),
          window.innerHeight,
          DATA_ROW_HEIGHT,
        ),
      rowHeights: { group: GROUP_ROW_HEIGHT, data: DATA_ROW_HEIGHT },
      run: (fn) => this.zone.run(fn),
      onFinish: () => this.zone.run(() => this.intro.set(null)),
    });
  }

  /** Gets the next grid (this one is about to be created, or the next visit's) to start from the flat list. */
  private armIntro(): void {
    const reduced = prefersReducedMotion();
    this.reducedFade.set(reduced);
    this.intro.set(reduced ? null : this.createIntro());
  }

  private onFirstDataRendered(api: GridApi): void {
    const intro = this.intro();
    if (intro) {
      this.zone.runOutsideAngular(() => this.startIntro(intro, api));
    }
  }

  // start() ends the intro itself even when a frame throws; this is for its cleanup failing too. Drop the intro
  // then, or the list would stay hidden for the rest of the visit (whatever grid state that cleanup didn't
  // reach is left as it is).
  private startIntro(intro: PastGroupIntro, api: GridApi): void {
    void intro.start(api).catch(() => this.dropIntro(intro));
  }

  // Stops an intro that must not run or finish and hands the grid its plain list, unless a newer intro replaced it.
  private dropIntro(intro: PastGroupIntro): void {
    intro.cancel();
    this.zone.run(() => {
      if (this.intro() === intro) {
        this.intro.set(null);
      }
    });
  }

  private guardAgainstNoRows(api: GridApi): void {
    const intro = this.intro();
    if (!intro) {
      return;
    }
    this.zone.runOutsideAngular(() =>
      setTimeout(() => {
        if (api.isDestroyed() || this.intro() !== intro || intro.hasStarted) {
          return;
        }
        if (api.getDisplayedRowCount() > 0) {
          this.startIntro(intro, api);
        } else {
          this.dropIntro(intro);
        }
      }, NO_ROWS_GRACE_MS),
    );
  }

  private onGridPreDestroyed(): void {
    this.gridApi = null;
    // Switched away, possibly mid-intro: stop it, and arm a fresh one for the next time the list is shown.
    this.intro()?.cancel();
    this.armIntro();
  }

  /**
   * Plays the intro again on the grid that is showing. A granularity the user picks regroups the list, which is a
   * good moment to show the grouping again; the views control calls this from its `granularityPicked` output,
   * which only a real pick in the dropdown emits (not the automatic reselection after the options change, nor a
   * filter or period change). Does nothing when no grid is showing (another tab) or under reduced motion.
   */
  replayIntro(): void {
    const api = this.gridApi;
    // An empty list has nothing to animate (guardAgainstNoRows drops the intro for it on the first visit), and
    // the skeleton would flash over it. Group rows count, so any list with data passes.
    if (!api || api.isDestroyed() || prefersReducedMotion() || !api.getDisplayedRowCount()) {
      return;
    }
    this.intro()?.cancel();
    this.armIntro();
    // ag-Grid caches whether an external filter is present and only re-reads it in onFilterChanged(). The last
    // intro ended by telling it there was none, so without this the new flat phase would render every row.
    api.onFilterChanged();
    // No rebuild here: the pick gave the rows a new granularity, so the template's [rowData] binding rebuilds the
    // grid on the next change detection, from the flat list armed above.
    this.zone.runOutsideAngular(() => this.startIntro(this.intro(), api));
  }

  private getPastTransactionTableColumns(): ColDef<TableRow>[] {
    const columns = getTransactionTableColumns();
    const amountCol = columns.find((c) => c.field === "amount");
    if (amountCol) {
      amountCol.width = 175;
      amountCol.cellRendererParams = { showGroceryIcon: true };
    }
    // cellClassRules are re-evaluated by api.refreshCells() without rebuilding the renderers; that is how the
    // intro fades a month's rows while they shrink.
    const introFade = {
      "past-intro-fade": (params: CellClassParams<TableRow>) =>
        !!this.intro()?.isCollapsing(params.node.parent?.key),
    };
    columns.forEach((column) => {
      column.cellClassRules = introFade;
    });
    return [
      {
        headerName: "Group",
        rowGroup: true,
        hide: true,
        valueGetter: (params: ValueGetterParams<TableRow>) => {
          const newGranularity =
            this.granularityFilter.selectedGranularity() as GranularityProperty;
          return this.getPastGranularityGroupKey(params.data?.transactionDate, newGranularity);
        },
      },
      ...columns,
    ];
  }

  private getPastGranularityGroupKey(dateString: string, granularity: GranularityProperty): string {
    if (!dateString || !granularity) {
      return "";
    }
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return "";
    }

    const formatters: Record<GranularityProperty, (date: Date) => string> = {
      day: (date) => this.datePipe.transform(date, "yyyy-MM-dd") || "",
      week: this.formatWeek.bind(this),
      month: (date) => this.datePipe.transform(date, "yyyy-MM") || "",
      quarter: this.formatQuarter.bind(this),
      year: (date) => this.datePipe.transform(date, "yyyy") || "",
    };

    return formatters[granularity](date);
  }

  private formatWeek(date: Date): string {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    const weekNo = Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
    return `${d.getUTCFullYear()}-W${String(weekNo).padStart(2, "0")}`;
  }

  private formatQuarter(date: Date): string {
    const quarter = Math.floor(date.getMonth() / 3) + 1;
    return `${date.getFullYear()}-Q${quarter}`;
  }
}
